import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, IconAddInCircle24, IconEdit24, IconAccountAvatarInactive32, Box, IconCloseX16, IconButton, Modal, Text } from '@stitch-fix/mode-react';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { useClientSelfieUpdateMutation, ClientSelfieUpdateMutation } from '../../graphql/clientSelfieUpdate';
import { PhotoUpload, PhotoUploadRenderProps } from '../../PhotoUpload/PhotoUpload';
import { useDeleteClientSelfieMutation } from '../../graphql/deleteClientSelfie';
import PhotoUploadErrorModal from '../../PhotoUploadErrorModal';
import styles from './Selfie.module.scss';
export interface SelfieUploadProps {
  existingImageUrl?: string | null;
  onChange?: () => void;
  showRemovalConfirmation?: boolean;
  /** When true, hides the inline add/edit control until an image exists (e.g. Android selfie-only layout uses a separate footer CTA). */
  hideInlineUploadActionsWhenEmpty?: boolean;
  onUploadReady?: (trigger: () => void) => void;
}
export const SelfieUpload: React.FC<SelfieUploadProps> = ({
  existingImageUrl,
  onChange,
  showRemovalConfirmation = false,
  hideInlineUploadActionsWhenEmpty = false,
  onUploadReady
}) => {
  const {
    reportSelectEvent
  } = useSelectEvent();
  const uploadClickRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (onUploadReady) {
      onUploadReady(() => uploadClickRef.current?.());
    }
  }, [onUploadReady]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageUploadError, setIsImageUploadError] = useState(false);
  const [imageUploadErrorMessage, setImageUploadErrorMessage] = useState('');
  const [isRemovalConfirmationOpen, setIsRemovalConfirmationOpen] = useState(false);
  const [deleteSelfie, {
    loading: isDeleting
  }] = useDeleteClientSelfieMutation();
  const [updateSelfie, {
    loading: selfieLoading
  }] = useClientSelfieUpdateMutation();
  const deleteSelfieImage = useCallback(() => {
    deleteSelfie({
      onCompleted: () => {
        onChange?.();
      }
    });
  }, [deleteSelfie, onChange]);
  const onDeleteClick = useCallback(() => {
    if (showRemovalConfirmation && existingImageUrl) {
      setIsRemovalConfirmationOpen(true);
      return;
    }
    deleteSelfieImage();
  }, [deleteSelfieImage, existingImageUrl, showRemovalConfirmation]);
  const handleConfirmDelete = useCallback(() => {
    setIsRemovalConfirmationOpen(false);
    deleteSelfieImage();
  }, [deleteSelfieImage]);
  const handleCloseRemovalConfirmation = useCallback(() => {
    setIsRemovalConfirmationOpen(false);
  }, []);
  const handleDismissUploadError = useCallback(() => {
    setIsImageUploadError(false);
    setImageUploadErrorMessage('');
  }, []);
  const handleUploadSuccess = useCallback((url: string) => {
    setIsProcessing(true);
    setIsUploading(false);
    updateSelfie({
      variables: {
        input: {
          selfieImageUrl: url
        }
      },
      onCompleted: (data: ClientSelfieUpdateMutation) => {
        const result = data?.clientSelfieUpdate?.result;
        if (result?.__typename === 'Client') {
          setIsProcessing(false);
          setIsImageUploadError(false);
          setImageUploadErrorMessage('');
          onChange?.();
        } else if (result?.__typename === 'ClientSelfieUpdateError') {
          setIsProcessing(false);
          setIsImageUploadError(true);
          setImageUploadErrorMessage(result.message || '');
        } else {
          setIsProcessing(false);
        }
      },
      onError: () => {
        setIsProcessing(false);
      }
    });
  }, [updateSelfie, onChange]);
  const handleUploadClick = () => {
    reportSelectEvent({
      schema: 'select',
      name: 'update_selfie_button',
      action_name: 'update_selfie'
    });
    setIsUploading(true);
    setIsImageUploadError(false);
  };
  const renderSelfieUpload = (props: PhotoUploadRenderProps) => {
    const {
      currentImageUrl,
      isButtonDisabled,
      buttonText,
      handleUploadClick: onUploadClick
    } = props;
    return <Box className={styles.container} style={{
      position: 'relative'
    }}>
        {currentImageUrl ? <>
            <Box className={styles.imageContainer}>
              <img src={currentImageUrl} alt="Self portrait" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
          }} />
            </Box>
            <Box style={{
          position: 'absolute',
          top: '10px',
          right: 'calc(50% - 85px)'
        }}>
              <IconButton variant="overlay" aria-label="Delete selfie" onClick={onDeleteClick}>
                <IconCloseX16 />
              </IconButton>
            </Box>
          </> : <Box className={styles.imagePlaceholderContainer} onClick={onUploadClick}>
            <Box style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          height: '150px',
          width: '150px'
        }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 190 190" fill="none" data-testid="circular-upload-area">
                <circle cx="95" cy="95" r="94.5" fill="#F0F1F3" stroke="#001E60" strokeLinecap="round" strokeDasharray="7 7" />
              </svg>
            </Box>
            <IconAccountAvatarInactive32 color="blue-45" title="Add headshot image" width={60} height={60} style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }} />
          </Box>}
        {(!hideInlineUploadActionsWhenEmpty || currentImageUrl) && <Box style={{
        position: 'absolute',
        bottom: '-20px',
        left: '50%',
        transform: 'translateX(-50%)'
      }}>
            <Button startIcon={currentImageUrl ? <IconEdit24 /> : <IconAddInCircle24 />} onClick={onUploadClick} disabled={isButtonDisabled} variant="filled-staple" width="fit">
              {buttonText}
            </Button>
          </Box>}
      </Box>;
  };
  return <PhotoUpload onUploadSuccess={handleUploadSuccess} onUploadError={() => setIsUploading(false)} initialUrl={existingImageUrl} isProcessing={isProcessing || selfieLoading || isDeleting} isUploading={isUploading} processingText={isDeleting ? 'Deleting...' : 'Loading...'} onUploadClick={handleUploadClick} onUploadCancel={() => setIsUploading(false)}>
      {/* Make PhotoUpload's handleUploadClick accessible to the modal */}
      {props => {
      uploadClickRef.current = props.handleUploadClick;
      return <>
            {renderSelfieUpload(props)}

            {isRemovalConfirmationOpen && <Modal isOpen heading="Are you sure?" onClose={handleCloseRemovalConfirmation} variant="slide-up-small">
                <Box mt={1}>
                  <Text setting="body-medium" spacingBottom>
                    If you remove your headshot, you won&apos;t receive new
                    Vision images.
                  </Text>
                  <Button variant="filled-statement" onClick={handleConfirmDelete}>
                    Confirm
                  </Button>
                </Box>
              </Modal>}

            {isImageUploadError && <PhotoUploadErrorModal onDismiss={() => {
          props.discardStagedUpload();
          handleDismissUploadError();
        }} onPhotoUpload={() => {
          handleDismissUploadError();
          props.handleUploadClick();
        }} errorMessage={imageUploadErrorMessage} />}
          </>;
    }}
    </PhotoUpload>;
};